import React, { useState, useEffect } from 'react';
import { clientes } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  Edit2, 
  Loader2,
  Save,
  Users
} from 'lucide-react';

export default function AdminClientes() {
  const [loading, setLoading] = useState(true);
  const [clientesList, setClientesList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    rfc: '',
    max_cuentas: 5,
    activo: true
  });

  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    setLoading(true);
    try {
      const response = await clientes.getAll();
      setClientesList(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await clientes.update(editingId, formData);
        toast.success('Cliente actualizado');
      } else {
        await clientes.create(formData);
        toast.success('Cliente creado');
      }
      loadClientes();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error al guardar cliente');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      rfc: '',
      max_cuentas: 5,
      activo: true
    });
    setEditingId(null);
  };

  const openEdit = (cliente) => {
    setFormData({
      nombre: cliente.nombre,
      rfc: cliente.rfc || '',
      max_cuentas: cliente.max_cuentas,
      activo: cliente.activo
    });
    setEditingId(cliente.id);
    setDialogOpen(true);
  };

  return (
    <div data-testid="admin-clientes-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Gestión de Clientes
            </h1>
            <p className="text-muted-foreground mt-1">
              Administra todos los clientes de la plataforma
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} data-testid="add-cliente-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nombre de la empresa *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    placeholder="Nombre de la empresa"
                    data-testid="cliente-nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label>RFC</Label>
                  <Input
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value })}
                    placeholder="RFC de la empresa"
                    data-testid="cliente-rfc"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Máximo de cuentas</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.max_cuentas}
                    onChange={(e) => setFormData({ ...formData, max_cuentas: parseInt(e.target.value) || 1 })}
                    data-testid="cliente-max-cuentas"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Activo</Label>
                  <Switch
                    checked={formData.activo}
                    onCheckedChange={(v) => setFormData({ ...formData, activo: v })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  <Save className="w-4 h-4 mr-2" />
                  Guardar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Clientes</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {clientesList.length}
                  </p>
                </div>
                <Building2 className="w-8 h-8 text-primary/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Activos</p>
                  <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {clientesList.filter(c => c.activo).length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Clientes Inactivos</p>
                  <p className="text-2xl font-bold text-muted-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {clientesList.filter(c => !c.activo).length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clients Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-40 rounded-xl"></div>
            ))
          ) : clientesList.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No hay clientes registrados</p>
              <p className="text-sm text-muted-foreground">Crea tu primer cliente para comenzar</p>
            </div>
          ) : (
            clientesList.map((cliente) => (
              <Card 
                key={cliente.id} 
                className={`card-hover ${!cliente.activo ? 'opacity-60' : ''}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{cliente.nombre}</CardTitle>
                        <p className="text-sm text-muted-foreground">{cliente.rfc || 'Sin RFC'}</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openEdit(cliente)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        Máx. {cliente.max_cuentas} cuentas
                      </span>
                    </div>
                    <Badge variant={cliente.activo ? 'default' : 'secondary'}>
                      {cliente.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
